/* Copyright (c) 2020-present CloudIO Inc */

import { getCustomChildItemTypes } from 'componentregistry/registry/Manager';
import { replaceItemType } from 'designer/state';
import { helpRenders } from '../../help/sample_pages/BoxPage';
import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './Box';
import { analyzeBox } from './BoxAnalyze';
import { BoxProps, isPropertyVisible } from './BoxExports';

const BoxManager: ComponentManagerFn<'Box'> = (
  platform: Platform,
  type: 'Box',
) => {
  return {
    ...baseComponentManager<'Box'>(platform, type),
    type,
    name: 'Box',
    isChildAllowed: (page, item, childType) =>
      (
        [
          ...getCustomChildItemTypes('Box'),
          'ActionButton',
          'ActiveUsers',
          'Alert',
          'AsyncSelect',
          'AttachmentField',
          'AutoSizer',
          'Avatar',
          'Box',
          'BoxList',
          'BreadcrumbNavigation',
          'Button',
          'ButtonDropdown',
          'ButtonGroup',
          'Checkbox',
          'Chip',
          'Chips',
          'ClientSideExcelUpload',
          'CollectionList',
          'ControllerComponent',
          'DarkModeToggle',
          'DateField',
          'Divider',
          'DragSource',
          'DragSourceBox',
          'DropTargetBox',
          'DropTarget',
          'EmptySpacer',
          'ExpressionWatchListener',
          'FullScreenToggle',
          'FusionChartsIO',
          'MultiSeriesChart',
          'GraphicMessage',
          'H1',
          'H2',
          'H3',
          'H4',
          'H5',
          'H6',
          'HTML',
          'IOSSwitch',
          'Icon',
          'IconButton',
          'IconSelect',
          'IconSwitch',
          'Image',
          'InputBase',
          'ImageCropper',
          'LanguageSelect',
          'Link',
          'LOVField',
          'LookupSelect',
          'Media',
          'MultiField',
          'Markdown',
          'Menu',
          'MetricBox',
          'Mirror',
          'MutliSelectLOVField',
          'PageMenu',
          'Pagination',
          'Popover',
          'ProgressIndicator',
          'PulseTip',
          'RadioBox',
          'RichTextEditor',
          'Rating',
          'Select',
          'SmartSearch',
          'Stepper',
          'SwitchRole',
          'TextField',
          'NumberField',
          'Toolbar',
          'TriggerOnClick',
          'Typography',
          'UploadFileToFunction',
          'UserList',
          'UserPic',
          'UserProfileMenu',
          'ViewAttachment',
          'WaveDots',
          'CalendarSourceEvent',
          'MultiDateSelector',
          'AngularGauge',
          'GridPersonalizationMenu',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    defaultChildren: ['Typography'],
    isPropertyVisible,
    props: BoxProps,
    category: 'Dashboard',
    canHaveChildren: () => true,
    onReplace: (item, fromItem, page) => {
      if (fromItem.type === 'RowPanel') {
        fromItem.children.forEach((child) => {
          if (page[child].type === 'ColumnPanel') {
            replaceItemType(page, child, 'Box');
          }
        });
      }
      return item;
    },
    prependOverlay: true,
    icon: { iconName: 'box' },
    showEmptyPlaceholderInDesignerView: false,
    replaceableWith: ['BoxList', 'DragSourceBox', 'DropTargetBox'],
    description: `The Box component serves as a wrapper component for most of the CSS utility needs.
      Use standard CSS attributes as the values for various Box component properties.`,
    helpRenders,
    analyze: analyzeBox,
  };
};

export default BoxManager;
